import { Controller } from '@hotwired/stimulus'
import flatpickr from 'flatpickr'
import { Portuguese } from 'flatpickr/dist/l10n/pt'
import ApplicationController from './application_controller'

export default class extends ApplicationController {
  static values = {
    reflexTarget: String,
    reflexAction: String,
    reflexStartDate: String,
    reflexEndDate: String,
    formSubmit: Boolean,
    mode: { type: String, default: 'range' },
  }

  fp = null
  styleElement = null

  connect() {
    super.connect()

    const existingCalendars = document.querySelectorAll('.flatpickr-calendar')
    existingCalendars.forEach((calendar) => calendar.remove())

    if (this.element._flatpickr) {
      this.element._flatpickr.destroy()
    }

    const oldStyle = document.getElementById('flatpickr-custom-styles')
    if (oldStyle) {
      oldStyle.remove()
    }

    const twoYearsAgo = new Date()
    twoYearsAgo.setFullYear(twoYearsAgo.getFullYear() - 2)

    const isSingleMode = this.modeValue === 'single'

    const defaultDates = []
    if (!isSingleMode && this.hasReflexStartDateValue && this.hasReflexEndDateValue) {
      defaultDates.push(this.reflexStartDateValue, this.reflexEndDateValue)
    }

    this.fp = flatpickr(this.element, {
      defaultDate: defaultDates.length ? defaultDates : undefined,
      mode: isSingleMode ? 'single' : 'range',
      enableTime: false,
      dateFormat: 'Y-m-d',
      altInput: true,
      altFormat: 'd/m/Y',
      allowInput: true,
      minDate: isSingleMode ? undefined : twoYearsAgo,
      closeOnSelect: isSingleMode,
      disableMobile: false,
      clickOpens: true,
      animate: true,
      calendarClass: 'custom-calendar',
      monthSelectorType: 'static',
      enableMonths: true,
      enableYears: true,
      monthSelectPosition: 'below',
      nextArrow:
        '<svg width="24" height="24" fill="currentColor" viewBox="0 0 20 20"><path fill-rule="evenodd" d="M7.293 14.707a1 1 0 010-1.414L10.586 10 7.293 6.707a1 1 0 011.414-1.414l4 4a1 1 0 010 1.414l-4 4a1 1 0 01-1.414 0z" clip-rule="evenodd"></path></svg>',
      prevArrow:
        '<svg width="24" height="24" fill="currentColor" viewBox="0 0 20 20"><path fill-rule="evenodd" d="M12.707 5.293a1 1 0 010 1.414L9.414 10l3.293 3.293a1 1 0 01-1.414 1.414l-4-4a1 1 0 010-1.414l4-4a1 1 0 011.414 0z" clip-rule="evenodd"></path></svg>',
      locale: Portuguese,
      rangeSeparator: isSingleMode ? undefined : ' até ',

      onChange: (selectedDates, dateStr, instance) => {
        if (isSingleMode) {
          if (selectedDates.length === 1) {
            this.element.dispatchEvent(new Event('change', { bubbles: true }))
            if (this.formSubmitValue) {
              const form = this.element.closest('form')
              if (form) form.submit()
            }
          }
        } else if (selectedDates.length === 2) {
          const startDate = selectedDates[0].toISOString().split('T')[0]
          const endDate = selectedDates[1].toISOString().split('T')[0]

          if (this.formSubmitValue) {
            const form = this.element.closest('form')
            if (form) {
              const startInput = form.querySelector('input[name="start_date"]')
              const endInput = form.querySelector('input[name="end_date"]')
              if (startInput) startInput.value = startDate
              if (endInput) endInput.value = endDate
              form.submit()
            }
          } else if (this.hasReflexTargetValue && this.hasReflexActionValue) {
            const reflexPath = `${this.reflexTargetValue}#${this.reflexActionValue}`
            this.stimulate(reflexPath, startDate, endDate)
          }
        }
      },

      onReady: (dateObj, dateStr, instance) => {
        this.styleElement = document.createElement('style')
        this.styleElement.id = 'flatpickr-custom-styles'
        this.styleElement.innerHTML = `
          .flatpickr-calendar {
            position: absolute;
            visibility: hidden;
            opacity: 0;
            background: #FFFBEB;
            text-align: center;
            width: 325px !important;
            padding: 1rem;
            display: none;
            animation: none;
            direction: ltr;
            border: none;
            font-size: 14px;
            line-height: 24px;
            border-radius: 0.5rem;
            box-sizing: border-box;
            touch-action: manipulation;
            box-shadow: 0 4px 6px -1px rgba(0, 0, 0, 0.1), 0 2px 4px -2px rgba(0, 0, 0, 0.1);
          }

          .flatpickr-calendar.open,
          .flatpickr-calendar.inline {
            opacity: 1;
            max-height: 640px;
            visibility: visible;
            display: inline-block;
            z-index: 99999;
          }

          .flatpickr-months {
            display: flex;
            background: #FFFBEB;
            padding: 0.5rem 0;
          }

          .flatpickr-month {
            display: flex;
            align-items: center;
            justify-content: center;
            background: #FFFBEB;
            color: rgba(0,0,0,0.9);
            fill: rgba(0,0,0,0.9);
            height: 34px;
            line-height: 1;
            text-align: center;
            position: relative;
            user-select: none;
            overflow: hidden;
            flex: 1;
            padding: 0.5rem 0;
          }

          .flatpickr-current-month {
            display: flex;
            align-items: center;
            justify-content: center;
            gap: 0.5rem;
            position: relative;
            text-align: center;
            height: 34px;
            padding: 0;
            width: 100%;
            left: 0;
            font-size: 1rem;
            line-height: 1;
            font-weight: 600;
            color: inherit;
            margin-bottom: 0.25rem;
          }

          .flatpickr-current-month .cur-month {
            margin-right: 5px;
            font-weight: 600;
          }

          .flatpickr-current-month .numInputWrapper {
            width: 70px;
            max-width: 70px;
            position: relative;
          }

          .flatpickr-current-month input.cur-year {
            background: transparent;
            box-sizing: border-box;
            color: inherit;
            cursor: text;
            margin: 0;
            display: inline-block;
            font-size: inherit;
            font-family: inherit;
            font-weight: 600;
            line-height: inherit;
            height: auto;
            border: 0;
            border-radius: 0;
            vertical-align: baseline;
            width: 100%;
            padding: 0;
            text-align: center;
            -webkit-appearance: textfield;
            -moz-appearance: textfield;
            appearance: textfield;
          }

          .flatpickr-current-month .numInputWrapper span.arrowUp,
          .flatpickr-current-month .numInputWrapper span.arrowDown {
            display: block;
            opacity: 1;
            position: absolute;
            right: 0;
            width: 14px;
            height: 50%;
            cursor: pointer;
            pointer-events: all;
          }

          .flatpickr-current-month .numInputWrapper span.arrowUp {
            top: -2px;
          }

          .flatpickr-current-month .numInputWrapper span.arrowDown {
            top: 50%;
          }

          .numInputWrapper span.arrowUp:after,
          .numInputWrapper span.arrowDown:after {
            content: "";
            display: block;
            border-left: 4px solid transparent;
            border-right: 4px solid transparent;
            position: absolute;
            left: 50%;
            transform: translateX(-50%);
          }

          .numInputWrapper span.arrowUp:after {
            border-bottom: 4px solid #F59E0B;
            bottom: 4px;
          }

          .numInputWrapper span.arrowDown:after {
            border-top: 4px solid #F59E0B;
            top: 4px;
          }

          .numInputWrapper span {
            opacity: 1 !important;
            border: none !important;
          }

          .numInputWrapper span:hover:after {
            border-bottom-color: #D97706;
            border-top-color: #D97706;
          }

          .flatpickr-weekdaycontainer {
            display: flex;
            width: 100%;
          }

          .flatpickr-weekdays {
            background: #FFFBEB;
            text-align: center;
            overflow: hidden;
            width: 100%;
            display: flex;
            align-items: center;
            height: 28px;
            margin-bottom: 0.5rem;
          }

          .flatpickr-weekday {
            flex: 1;
            font-size: 90%;
            background: #FFFBEB;
            color: rgba(0,0,0,0.54);
            line-height: 1;
            margin: 0;
            text-align: center;
            display: block;
            font-weight: bolder;
            padding: 0;
            height: 28px;
            line-height: 28px;
          }

          .dayContainer {
            padding: 0;
            outline: 0;
            text-align: left;
            width: 307.875px;
            min-width: 307.875px;
            max-width: 307.875px;
            box-sizing: border-box;
            display: inline-block;
            display: flex;
            flex-wrap: wrap;
            justify-content: space-around;
            transform: translate3d(0px, 0px, 0px);
            opacity: 1;
          }

          .flatpickr-day {
            background: none;
            border: 1px solid transparent;
            border-radius: 150px;
            box-sizing: border-box;
            color: #393939;
            cursor: pointer;
            font-weight: 400;
            width: 14.2857143%;
            flex-basis: 14.2857143%;
            max-width: 39px;
            height: 39px;
            line-height: 39px;
            margin: 0;
            display: inline-block;
            position: relative;
            justify-content: center;
            text-align: center;
          }

          .flatpickr-day.selected,
          .flatpickr-day.startRange,
          .flatpickr-day.endRange {
            background: #FCD34D;
            border-color: #F59E0B;
            color: black;
            font-weight: 600;
          }

          .flatpickr-day.inRange {
            background: #FEF3C7;
            border-color: #FDE68A;
            color: black;
            box-shadow: -5px 0 0 #FEF3C7, 5px 0 0 #FEF3C7;
          }

          .flatpickr-day:hover {
            background: #FDE68A;
            border-color: #FDE68A;
            cursor: pointer;
          }

          .flatpickr-months .flatpickr-prev-month,
          .flatpickr-months .flatpickr-next-month {
            cursor: pointer;
          }

          .flatpickr-months .flatpickr-prev-month:hover svg,
          .flatpickr-months .flatpickr-next-month:hover svg {
            fill: #F59E0B;
          }

          .flatpickr-input[readonly] {
            cursor: pointer;
            background-color: white;
          }

          /* Esconde as setas do input number */
          .flatpickr-current-month input.cur-year::-webkit-outer-spin-button,
          .flatpickr-current-month input.cur-year::-webkit-inner-spin-button {
            -webkit-appearance: none;
            margin: 0;
          }

          .flatpickr-current-month input.cur-year[type=number] {
            -moz-appearance: textfield;
          }
        `
        document.head.appendChild(this.styleElement)
      },
    })
  }

  disconnect() {
    if (this.fp) {
      this.fp.destroy()
      this.fp = null
    }

    if (this.styleElement) {
      this.styleElement.remove()
      this.styleElement = null
    }

    const existingCalendars = document.querySelectorAll('.flatpickr-calendar')
    existingCalendars.forEach((calendar) => calendar.remove())

    super.disconnect()
  }
}
